import { OktaContext } from '@okta/okta-react';
import { Navigate } from '@rocketlane/rocket-router';
import React, { useEffect, Suspense, useContext } from 'react';
import { AccountAPI } from 'api/account';
import { PageLoading } from 'components/atoms/loading';
import { LoginPage } from 'components/organisms/login/login';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, TokenType } from 'services';
import { logout } from 'utils/logout';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { getTokenMeta } from 'utils/token';
import { LoginWithToken } from '../login-with-token';

const TokenError = React.lazy(() =>
  import('components/organisms/token-error/token-error').then((d) => ({
    default: d.TokenError,
  })),
);

export const Login = () => {
  const oktaContext = useContext(OktaContext);
  const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
  const authState = oktaContext
    ? oktaContext.authState
    : { isAuthenticated: customIsAuthenticated };
  const { data: accountMeta } = useAccountMetaQuery();
  const params = useQueryParams();
  const token = params.get('token') ?? '';
  const cognitoType = params.get('cognito') ?? '';
  const extToken = params.get('rl-token') ?? '';
  const overrideAuth = params.get('overrideAuth') ?? false;
  const { isExpired, decoded } = getTokenMeta(token);

  useEffect(() => {
    if (
      accountMeta?.okta?.cookiesEnabled &&
      accountMeta?.okta?.clientId &&
      authState?.isAuthenticated === false
    ) {
      ActiveTab.save();
      redirectToOAuth(accountMeta.okta.clientId);
    }
  }, [
    accountMeta?.okta?.clientId,
    accountMeta?.okta?.cookiesEnabled,
    authState?.isAuthenticated,
  ]);

  if (decoded && isExpired) {
    return (
      <Suspense fallback={<PageLoading />}>
        <TokenError
          status={TokenErrorStatus.Expired}
          email={decoded.emailId}
          resendEmail={() =>
            AccountAPI.forgotAccount({ email: decoded.emailId })
          }
        />
      </Suspense>
    );
  }

  if (overrideAuth) {
    logout(`?token=${token}`);

    return <PageLoading />;
  }

  return !authState ? (
    <PageLoading />
  ) : authState.isAuthenticated ? (
    <Navigate
      replace
      to={
        /**
         * Active tab saves the entire url, not the pathname alone
         * TODO: remove URL construct when saving the pathname instead of url
         */
        new URL(ActiveTab.pop() ?? window?.location?.origin).href.replace(
          window?.location?.origin,
          '',
        )
      }
    />
  ) : token || extToken ? (
    <LoginWithToken
      token={token || extToken || ''}
      tokenType={
        extToken
          ? TokenType.External
          : cognitoType
          ? TokenType.Cognito
          : TokenType.Rocketlane
      }
    />
  ) : (
    <LoginPage />
  );
};
